import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { 
  Megaphone, Save, Eye, EyeOff, Palette, Link2, 
  Calendar, Sparkles, Loader2, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ConfigBannerFlotante = () => {
  const [config, setConfig] = useState({
    activo: false,
    texto: '¡Registraciones Abiertas!',
    texto_secundario: '',
    link: '',
    color_fondo: '#6366f1',
    color_texto: '#ffffff',
    estilo_animacion: 'pulse',
    posicion: 'top-right',
    fecha_inicio: '',
    fecha_fin: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/banner-flotante`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (response.data) {
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/banner-flotante`, config, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      toast.success('Banner actualizado correctamente');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const predefinedColors = [
    { name: 'Índigo', value: '#6366f1' },
    { name: 'Verde', value: '#22c55e' },
    { name: 'Rojo', value: '#ef4444' },
    { name: 'Naranja', value: '#f97316' },
    { name: 'Azul', value: '#3b82f6' },
    { name: 'Rosa', value: '#ec4899' },
    { name: 'Morado', value: '#a855f7' },
    { name: 'Dorado', value: '#eab308' }
  ];

  const animationStyles = [
    { value: 'pulse', label: 'Pulso Brillante', icon: '✨' },
    { value: 'bounce', label: 'Rebote Suave', icon: '🔄' },
    { value: 'shake', label: 'Vibración', icon: '📳' },
    { value: 'sparkle', label: 'Destello', icon: '💫' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-indigo-600" />
            Banner Flotante
          </h1>
          <p className="text-gray-600">Configura el banner promocional de la landing page</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={config.activo ? "default" : "secondary"} className={config.activo ? "bg-green-500" : ""}>
            {config.activo ? 'Activo' : 'Inactivo'}
          </Badge>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Guardar Cambios
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <div className="space-y-6">
          {/* Activación */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                {config.activo ? <Eye className="w-5 h-5 text-green-500" /> : <EyeOff className="w-5 h-5 text-gray-400" />}
                Activación
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Mostrar banner en landing page</p>
                  <p className="text-sm text-gray-500">El banner será visible para todos los visitantes</p>
                </div>
                <Switch
                  checked={config.activo}
                  onCheckedChange={(checked) => setConfig({ ...config, activo: checked })}
                  data-testid="banner-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Contenido */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                Contenido
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="texto">Texto Principal *</Label>
                <Input
                  id="texto"
                  value={config.texto}
                  onChange={(e) => setConfig({ ...config, texto: e.target.value })}
                  placeholder="Ej: ¡Registraciones Abiertas!"
                  maxLength={50}
                  data-testid="banner-text-input"
                />
                <p className="text-xs text-gray-500 mt-1">{config.texto.length}/50 caracteres</p>
              </div>

              <div>
                <Label htmlFor="texto_secundario">Texto Secundario (opcional)</Label>
                <Input
                  id="texto_secundario"
                  value={config.texto_secundario}
                  onChange={(e) => setConfig({ ...config, texto_secundario: e.target.value })}
                  placeholder="Ej: Inscríbete ahora - Cupos limitados"
                  maxLength={80}
                />
              </div>

              <div>
                <Label htmlFor="link" className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Link (opcional)
                </Label>
                <Input
                  id="link"
                  value={config.link}
                  onChange={(e) => setConfig({ ...config, link: e.target.value })}
                  placeholder="Ej: /registro o https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Si se agrega, el banner será clickeable</p>
              </div>
            </CardContent>
          </Card>

          {/* Estilo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Palette className="w-5 h-5 text-pink-500" />
                Estilo Visual
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Color de Fondo</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {predefinedColors.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setConfig({ ...config, color_fondo: color.value })}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        config.color_fondo === color.value 
                          ? 'border-gray-800 scale-110 shadow-lg' 
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                  <div className="flex items-center gap-2 ml-2">
                    <Input
                      type="color"
                      value={config.color_fondo}
                      onChange={(e) => setConfig({ ...config, color_fondo: e.target.value })}
                      className="w-10 h-10 p-1 cursor-pointer"
                    />
                    <span className="text-xs text-gray-500">Personalizado</span>
                  </div>
                </div>
              </div>

              <div>
                <Label>Color del Texto</Label>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => setConfig({ ...config, color_texto: '#ffffff' })}
                    className={`px-4 py-2 rounded-lg border-2 ${
                      config.color_texto === '#ffffff' ? 'border-gray-800' : 'border-gray-200'
                    } bg-white text-gray-800`}
                  >
                    Blanco
                  </button>
                  <button
                    onClick={() => setConfig({ ...config, color_texto: '#000000' })}
                    className={`px-4 py-2 rounded-lg border-2 ${
                      config.color_texto === '#000000' ? 'border-gray-800' : 'border-gray-200'
                    } bg-gray-800 text-white`}
                  >
                    Negro
                  </button>
                </div>
              </div>

              <div>
                <Label>Estilo de Animación</Label>
                <Select 
                  value={config.estilo_animacion} 
                  onValueChange={(v) => setConfig({ ...config, estilo_animacion: v })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {animationStyles.map((style) => (
                      <SelectItem key={style.value} value={style.value}>
                        {style.icon} {style.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Programación */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-blue-500" />
                Programación (Opcional)
              </CardTitle>
              <CardDescription>
                Deja en blanco para mostrar siempre mientras esté activo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fecha_inicio">Fecha de Inicio</Label>
                  <Input
                    id="fecha_inicio"
                    type="datetime-local"
                    value={config.fecha_inicio ? config.fecha_inicio.slice(0, 16) : ''}
                    onChange={(e) => setConfig({ ...config, fecha_inicio: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  />
                </div>
                <div>
                  <Label htmlFor="fecha_fin">Fecha de Fin</Label>
                  <Input
                    id="fecha_fin"
                    type="datetime-local"
                    value={config.fecha_fin ? config.fecha_fin.slice(0, 16) : ''}
                    onChange={(e) => setConfig({ ...config, fecha_fin: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  />
                </div>
              </div>
              {(config.fecha_inicio || config.fecha_fin) && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setConfig({ ...config, fecha_inicio: '', fecha_fin: '' })}
                >
                  <X className="w-4 h-4 mr-2" />
                  Limpiar fechas
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Preview Panel */}
        <div className="lg:sticky lg:top-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Vista Previa
              </CardTitle>
              <CardDescription>
                Así se verá el banner en la landing page
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="relative bg-gray-100 rounded-xl p-8 min-h-[300px] overflow-hidden"
                style={{
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
                }}
              >
                {/* Simulated header */}
                <div className="absolute top-0 left-0 right-0 h-16 bg-white/80 backdrop-blur border-b flex items-center px-4">
                  <div className="w-10 h-10 bg-indigo-600 rounded-lg"></div>
                  <div className="ml-3 space-y-1">
                    <div className="w-24 h-3 bg-gray-300 rounded"></div>
                    <div className="w-16 h-2 bg-gray-200 rounded"></div>
                  </div>
                </div>

                {/* Diagonal Ribbon Preview */}
                {config.activo && (
                  <div 
                    className="absolute top-0 left-0"
                    style={{ width: '120px', height: '120px' }}
                  >
                    <div
                      className="absolute animate-pulse"
                      style={{
                        top: '22px',
                        left: '-28px',
                        width: '140px',
                        transform: 'rotate(-45deg)',
                        backgroundColor: config.color_fondo,
                        color: config.color_texto,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        textAlign: 'center',
                        padding: '6px 8px'
                      }}
                    >
                      <span 
                        className="text-[10px] font-bold uppercase tracking-wide block"
                        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                      >
                        {config.texto || 'Texto'}
                      </span>
                      {config.texto_secundario && (
                        <span className="text-[8px] opacity-90 block">
                          {config.texto_secundario}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {!config.activo && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <EyeOff className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Banner desactivado</p>
                      <p className="text-sm">Actívalo para ver la vista previa</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="mt-4 bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Megaphone className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Consejos:</p>
                  <ul className="space-y-1 text-blue-700">
                    <li>• Usa textos cortos y llamativos</li>
                    <li>• Los colores contrastantes llaman más atención</li>
                    <li>• El visitante puede cerrar el banner (se recuerda por sesión)</li>
                    <li>• Programa fechas para campañas temporales</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ConfigBannerFlotante;
